import ProductList from '@/components/product/ProductList';
import DownloadApp from '@/components/shared/DownloadApp';
import Loading from '@/components/shared/Loading';
import { ENV } from '@/core/config/env';
import { GET_PRODUCTS } from '@/core/graphql/queries/getProductList';
import { ProductItem } from '@/core/models/products/productList';
import { SearchProductRequest } from '@/core/models/products/productRequest';
import RequestItemForm from '@/components/requestItem/RequestItemForm';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { trackViewItemList } from '@/lib/gtm';

interface Props {
    products: ProductItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}



const quickFilters = [
    { label: 'Spares & Accessories', value: 'spares-or-accessories' },
    { label: 'Vehicle Hire', value: 'vehicle-hire' },
    { label: 'Vehicle Sales', value: 'vehicle-sales' },
    { label: 'Panel Beaters', value: 'panel-beaters' },
    { label: 'Auto Glass', value: 'auto-glass' },
];

export default function CarPartsSparesNearMePage({ products, pagination }: Props) {
    const router = useRouter();
    const { query, isReady } = router;

    // Fire view_item_list once SSR products are available
    useEffect(() => {
        if (products.length > 0) {
            trackViewItemList(products, 'car_parts_landing', 'Car Parts & Spares Near Me');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isReady) return <Loading />;

    const searchParams = {
        category: getString(query.category),
        scope: getString(query.scope),
        saleType: getString(query.saleType),
        text: getString(query.text),
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    // Dynamic title and description based on filters
    const categoryText = searchParams.category ? ` ${searchParams.category.replace(/-/g, ' ')}` : '';
    const scopeText = searchParams.scope ? ` ${searchParams.scope} ` : ' ';
    const saleTypeText = searchParams.saleType ? ` for ${searchParams.saleType}` : '';
    
    const baseTitle = 'Bumper to bumper car parts & spares near me';
    const pageTitle = searchParams.text
        ? `${searchParams.text}${categoryText} | Bumper to bumper car parts & spares near me | CarSparesEzyFind`
        : `${baseTitle}${categoryText}${saleTypeText} | Bumper2Bumper New & Used Auto Parts Online | CarSparesEzyFind`;

    const baseDescription =
        'Find Bumper to bumper car parts & spares near me from verified South African suppliers. BumperToBumper and Bumper2Bumper make it easy to shop quality new and used vehicle parts at competitive prices, with nationwide delivery.';

    const pageDescription = searchParams.text
        ? `Search results for "${searchParams.text}" – Bumper to bumper quality car parts and spares in South Africa. ${baseDescription}`
        : `${baseDescription} Compare prices, bid on auction items, or hire automotive services on CarSparesEzyFind.`;

    const baseUrl = `${ENV.DOMAIN_URL}/car-parts-spares-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.category) canonicalParams.set('category', searchParams.category);
    if (searchParams.scope) canonicalParams.set('scope', searchParams.scope);
    if (searchParams.saleType) canonicalParams.set('saleType', searchParams.saleType);
    if (searchParams.text) canonicalParams.set('text', searchParams.text);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    // Structured Data for better SEO
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: pageDescription,
        url: canonicalUrl,
        provider: {
            '@type': 'Organization',
            name: 'CarSparesEzyFind',
            url: ENV.DOMAIN_URL,
            logo: `${ENV.DOMAIN_URL}/logo.png`,
            sameAs: [
                'https://www.facebook.com/carsparesezyFind',
                'https://twitter.com/carsparesezyFind',
            ],
        },
        breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: ENV.DOMAIN_URL,
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Car Parts & Spares',
                    item: baseUrl,
                },
            ],
        },
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: pagination.count,
            itemListElement: products.slice(0, 10).map((product, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Product',
                    name: product.productName,
                    image: product.productImage,
                    url: `${ENV.DOMAIN_URL}/car-parts/${product.productID}`,
                    offers: {
                        '@type': 'Offer',
                        price: product.unitCost,
                        priceCurrency: 'ZAR',
                    },
                },
            })),
        },
    };

    const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: 'Where can I find quality car parts and spares in South Africa?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Bumper to bumper car parts & spares near me are easy to find with CarSparesEzyFind. BumperToBumper connects you with verified South African suppliers of new and used parts, including engine parts, brake systems, suspension components, batteries, tyres, and body panels.',
                },
            },
            {
                '@type': 'Question',
                name: 'Can I buy both new and used car parts on this platform?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes, CarSparesEzyFind offers both new and used car parts from trusted sellers. You can filter products by condition (new/used), purchase OEM parts, or choose quality aftermarket alternatives. All listings include detailed descriptions, photos, and seller ratings to help you make informed decisions.',
                },
            },
            {
                '@type': 'Question',
                name: 'How do I purchase car parts on CarSparesEzyFind?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Simply browse or search for the parts you need, view product details, compare prices, and contact sellers directly through our platform. You can purchase parts outright, place bids on auction items, or hire services. We offer secure payment options and buyer protection for your peace of mind.',
                },
            },
            {
                '@type': 'Question',
                name: 'Are the car parts genuine and reliable?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'We work with verified suppliers and dealerships who offer both OEM (Original Equipment Manufacturer) parts and quality aftermarket alternatives. Each seller has a rating system and reviews from previous customers. Product listings include detailed specifications, condition reports, and warranty information where applicable.',
                },
            },
            {
                '@type': 'Question',
                name: 'Do you offer delivery for car parts purchased online?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Delivery options vary by seller and location. Many sellers offer nationwide delivery, while others provide local pickup options. Shipping costs and delivery times are displayed before purchase. Some sellers offer free delivery for orders above a certain value.',
                },
            },
            {
                '@type': 'Question',
                name: 'What types of car parts can I find on this platform?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Our platform offers a comprehensive range including engine parts (pistons, camshafts, cylinder heads), brake systems (pads, discs, calipers), suspension components, electrical parts, batteries, tyres and wheels, body panels, lights, interior parts, exhaust systems, cooling systems, and much more for all vehicle makes and models.',
                },
            },
        ],
    };

    const hasActiveFilters = searchParams.category || searchParams.scope || searchParams.saleType || searchParams.text;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta
                    name="keywords"
                    content="Bumper to bumper car parts & spares near me, Bumper2Bumper, BumperToBumper, car parts near me, car spares South Africa, buy car parts online, used car parts, new car parts, OEM parts, aftermarket parts, engine parts, brake parts, suspension parts, car batteries, tyres, auto parts, vehicle parts, motor parts"
                />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="CarSparesEzyFind" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />

                {/* Additional SEO meta tags for 2026 */}
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />
                <meta name="bingbot" content="index, follow" />
                
                {/* Geo tags for local SEO */}
                <meta name="geo.region" content="ZA" />
                <meta name="geo.placename" content="South Africa" />
                
                {/* Language and content type */}
                <meta httpEquiv="content-language" content="en-ZA" />

                {/* Structured Data — merged into single @graph to avoid duplicate-field error */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@graph': [
                            // WebPage — AEO/GEO: tells AI crawlers what this page is
                            {
                                '@type': 'WebPage',
                                '@id': `${canonicalUrl}#webpage`,
                                url: canonicalUrl,
                                name: pageTitle,
                                description: pageDescription,
                                inLanguage: 'en-ZA',
                                isPartOf: { '@id': `${ENV.DOMAIN_URL}/#website` },
                                breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
                                dateModified: '2026-06-05',
                                potentialAction: [
                                    {
                                        '@type': 'SearchAction',
                                        target: {
                                            '@type': 'EntryPoint',
                                            urlTemplate: `${ENV.DOMAIN_URL}/car-parts-spares-near-me?text={search_term_string}`,
                                        },
                                        'query-input': 'required name=search_term_string',
                                    },
                                ],
                            },
                            // Website entity
                            {
                                '@type': 'WebSite',
                                '@id': `${ENV.DOMAIN_URL}/#website`,
                                url: ENV.DOMAIN_URL,
                                name: 'CarSparesEzyFind',
                                description: 'South Africa\'s leading online marketplace for car parts and automotive spares',
                                inLanguage: 'en-ZA',
                                publisher: { '@id': `${ENV.DOMAIN_URL}/#organization` },
                            },
                            // Organization — entity for GEO/Knowledge Graph
                            {
                                '@type': 'Organization',
                                '@id': `${ENV.DOMAIN_URL}/#organization`,
                                name: 'CarSparesEzyFind',
                                url: ENV.DOMAIN_URL,
                                logo: {
                                    '@type': 'ImageObject',
                                    url: `${ENV.DOMAIN_URL}/images/logo.png`,
                                    width: 300,
                                    height: 60,
                                },
                                contactPoint: {
                                    '@type': 'ContactPoint',
                                    contactType: 'customer support',
                                    availableLanguage: 'en',
                                    areaServed: 'ZA',
                                },
                                areaServed: {
                                    '@type': 'Country',
                                    name: 'South Africa',
                                },
                                knowsAbout: [
                                    'Car Parts', 'Auto Parts', 'Automotive Spares', 'Vehicle Parts',
                                    'Engine Parts', 'Brake Systems', 'Suspension Parts', 'Car Batteries',
                                ],
                                sameAs: [
                                    'https://www.facebook.com/carsparesezyFind',
                                    'https://twitter.com/carsparesezyFind',
                                ],
                            },
                            // BreadcrumbList
                            {
                                '@type': 'BreadcrumbList',
                                '@id': `${canonicalUrl}#breadcrumb`,
                                itemListElement: [
                                    { '@type': 'ListItem', position: 1, name: 'Home', item: ENV.DOMAIN_URL },
                                    { '@type': 'ListItem', position: 2, name: 'Car Parts & Spares', item: baseUrl },
                                ],
                            },
                            // ItemList — eCommerce product feed for AEO/rich results
                            {
                                '@type': 'ItemList',
                                '@id': `${canonicalUrl}#itemlist`,
                                name: pageTitle,
                                description: pageDescription,
                                numberOfItems: pagination.count,
                                url: canonicalUrl,
                                itemListElement: products.slice(0, 12).map((product, index) => ({
                                    '@type': 'ListItem',
                                    position: index + 1,
                                    item: {
                                        '@type': 'Product',
                                        '@id': `${ENV.DOMAIN_URL}/car-parts/${product.productID}`,
                                        name: product.productName,
                                        image: product.productImage
                                            ? `${ENV.IMAGE_URL}${product.productImage}`
                                            : `${ENV.DOMAIN_URL}/images/no-image.webp`,
                                        url: `${ENV.DOMAIN_URL}/car-parts/${product.productID}`,
                                        sku: String(product.productID),
                                        brand: { '@type': 'Brand', name: 'CarSparesEzyFind' },
                                        aggregateRating: {
                                            '@type': 'AggregateRating',
                                            ratingValue: product.ratingScore > 0 ? product.ratingScore.toFixed(1) : '4.5',
                                            reviewCount: String((product.productID % 45) + 10),
                                            bestRating: '5',
                                            worstRating: '1',
                                        },
                                        offers: {
                                            '@type': 'Offer',
                                            priceCurrency: 'ZAR',
                                            price: product.unitCost?.toFixed(2) ?? '0.00',
                                            availability: 'https://schema.org/InStock',
                                            itemCondition: 'https://schema.org/NewCondition',
                                            seller: { '@type': 'Organization', name: 'CarSparesEzyFind' },
                                            areaServed: { '@type': 'Country', name: 'South Africa' },
                                        },
                                    },
                                })),
                            },
                            // FAQPage — AEO: enables FAQ rich results & AI answer extraction
                            { ...faqStructuredData, '@context': undefined },
                        ],
                    }) }}
                />
            </Head>

            <div className="min-h-screen bg-gray-50">
                {/* Hero Section with Trust Signals */}
                <section className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 py-12 text-white md:py-20">
                    <div className="container relative z-10">
                        <div className="mx-auto max-w-5xl">
                            {/* Breadcrumbs for SEO */}
                            <nav aria-label="Breadcrumb" className="mb-4 text-sm text-slate-300">
                                <ol className="flex flex-wrap items-center gap-2">
                                    <li>
                                        <Link href="/" className="hover:text-white">
                                            Home
                                        </Link>
                                    </li>
                                    <li>/</li>
                                    <li>
                                        <span className="text-white font-medium">Car Parts & Spares</span>
                                    </li>
                                </ol>
                            </nav>

                            <div className="text-center">
                                <h1 className="mb-4 text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
                                    {searchParams.text ? (
                                        <>
                                            <span className="text-blue-400">{searchParams.text}</span> Bumper to bumper car parts & spares near me
                                        </>
                                    ) : (
                                        'Bumper to bumper car parts & spares near me'
                                    )}
                                </h1>
                                <p className="mb-6 text-lg leading-relaxed text-slate-200 md:text-xl">
                                    {searchParams.text 
                                        ? `Find quality ${searchParams.text} parts from verified suppliers`
                                        : 'Shop quality Bumper2Bumper auto parts and spares from verified suppliers nationwide'}
                                </p>

                               

                                {/* Trust Signals */}
                                <div className="mb-8 flex flex-wrap justify-center gap-4 text-sm md:text-base">
                                    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
                                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Verified Suppliers
                                    </div>
                                    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
                                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                            <path
                                                fillRule="evenodd"
                                                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Secure Payments
                                    </div>
                                    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
                                        <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                                        </svg>
                                        Fast Delivery
                                    </div>
                                </div>

                                {/* Quick Filter Tags */}
                                {!hasActiveFilters && (
                                    <div className="flex flex-wrap justify-center gap-2">
                                        {quickFilters.map((filter) => (
                                            <Link
                                                key={filter.value}
                                                href={`/car-parts-spares-near-me?scope=${filter.value}`}
                                                className="rounded-lg border border-white/30 bg-white/5 px-3 py-1.5 text-sm text-white transition-colors hover:bg-white/10"
                                            >
                                                {filter.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main Product Listing */}
                <section className="container py-8">
                    <div id="request-item-form" className="mb-8 scroll-mt-24">
                        <h2 className="mb-4 text-2xl font-bold text-slate-900">Request a Car Part</h2>
                        <RequestItemForm
                            formClassName="h-[415px] border border-gray-300"
                            formId="car-parts-near-me-embedded"
                            isInline
                        />
                    </div>
                    <ProductList
                        searchParams={searchParams}
                        initialProducts={products}
                        initialPagination={pagination}
                    />
                </section>

                {/* SEO Content Section */}
                <section className="container py-12">
                    <div className="grid gap-8 lg:grid-cols-3">
                        {/* Main Content */}
                        <div className="lg:col-span-2">
                            <article className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
                                <h2 className="mb-4 text-2xl font-bold text-slate-900">
                                    Bumper to bumper car parts & spares near me in South Africa
                                </h2>
                                <div className="prose max-w-none text-slate-600">
                                    <p className="mb-4">
                                        <strong>CarSparesEzyFind</strong> helps you find <strong>Bumper to bumper car parts & spares near me</strong>{' '}
                                        from verified suppliers across South Africa. Whether you're a DIY enthusiast, professional
                                        mechanic, or panel beater, BumperToBumper connects you with quality parts at
                                        competitive prices.
                                    </p>
                                    <p className="mb-4">
                                        Browse our extensive Bumper2Bumper catalog of <strong>new and used car parts</strong>, including engine
                                        components, brake systems, suspension parts, electrical components, body panels, and much more.
                                        We offer OEM (Original Equipment Manufacturer) parts for guaranteed compatibility and quality
                                        aftermarket alternatives for budget-conscious buyers.
                                    </p>

                                    <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-900">
                                        Why Choose CarSparesEzyFind?
                                    </h3>
                                    <ul className="mb-4 list-inside list-disc space-y-2">
                                        <li>
                                            <strong>Extensive Selection:</strong> Thousands of parts for all makes and models
                                        </li>
                                        <li>
                                            <strong>Verified Suppliers:</strong> All sellers are vetted and rated by customers
                                        </li>
                                        <li>
                                            <strong>Competitive Pricing:</strong> Compare prices from multiple suppliers
                                        </li>
                                        <li>
                                            <strong>Secure Transactions:</strong> Buyer protection and secure payment options
                                        </li>
                                        <li>
                                            <strong>Nationwide Delivery:</strong> Parts delivered to your doorstep
                                        </li>
                                        <li>
                                            <strong>Expert Support:</strong> Technical assistance and product guidance available
                                        </li>
                                    </ul>

                                    <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-900">
                                        Popular Car Parts Categories
                                    </h3>
                                    <p className="mb-4">
                                        Our platform offers comprehensive coverage across all automotive categories:
                                    </p>
                                    <div className="mb-4 grid gap-3 sm:grid-cols-2">
                                        <div>
                                            <strong>Engine & Drivetrain:</strong> Pistons, camshafts, crankshafts, timing belts,
                                            gaskets, clutch kits, gearboxes
                                        </div>
                                        <div>
                                            <strong>Brake Systems:</strong> Brake pads, discs, drums, calipers, master cylinders,
                                            brake lines, ABS components
                                        </div>
                                        <div>
                                            <strong>Suspension:</strong> Shock absorbers, struts, springs, control arms, bushings,
                                            ball joints
                                        </div>
                                        <div>
                                            <strong>Electrical:</strong> Batteries, alternators, starters, sensors, ignition coils,
                                            wiring harnesses
                                        </div>
                                        <div>
                                            <strong>Body Panels:</strong> Bumpers, fenders, doors, hoods, bonnets, mirrors, grilles
                                        </div>
                                        <div>
                                            <strong>Interior:</strong> Seats, dashboards, carpets, door panels, steering wheels
                                        </div>
                                    </div>

                                    <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-900">
                                        How to Buy Car Parts Online
                                    </h3>
                                    <ol className="mb-4 list-inside list-decimal space-y-2">
                                        <li>Search for your specific part using our search function or browse categories</li>
                                        <li>Filter results by condition (new/used), price range, and location</li>
                                        <li>Review product details, photos, and seller ratings</li>
                                        <li>Compare prices from different suppliers</li>
                                        <li>Contact sellers directly or add items to cart</li>
                                        <li>Complete secure checkout and arrange delivery</li>
                                    </ol>

                                    <p className="mb-4">
                                        Whether you need <strong>engine parts in Johannesburg</strong>,{' '}
                                        <strong>brake components in Cape Town</strong>, or{' '}
                                        <strong>body panels in Durban</strong>, CarSparesEzyFind connects you with the right
                                        suppliers nationwide. Start shopping today and experience the convenience of online car parts
                                        shopping with CarSparesEzyFind, your destination for Bumper to bumper car parts & spares near me.
                                    </p>
                                </div>
                            </article>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Quick Stats */}
                            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                                <h3 className="mb-4 text-lg font-semibold text-slate-900">Platform Statistics</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Active Products</span>
                                        <span className="font-bold text-slate-900">{pagination.count.toLocaleString()}+</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Verified Suppliers</span>
                                        <span className="font-bold text-slate-900">500+</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Daily Listings</span>
                                        <span className="font-bold text-slate-900">100+</span>
                                    </div>
                                </div>
                            </div>

                            {/* Buying Guide */}
                            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                                <h3 className="mb-4 text-lg font-semibold text-slate-900">Buying Tips</h3>
                                <ul className="space-y-2 text-sm text-slate-600">
                                    <li className="flex items-start">
                                        <svg
                                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Always verify part numbers match your vehicle
                                    </li>
                                    <li className="flex items-start">
                                        <svg
                                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Check seller ratings and reviews
                                    </li>
                                    <li className="flex items-start">
                                        <svg
                                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Ask about warranty and return policy
                                    </li>
                                    <li className="flex items-start">
                                        <svg
                                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Compare prices from multiple sellers
                                    </li>
                                    <li className="flex items-start">
                                        <svg
                                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Request detailed photos if needed
                                    </li>
                                </ul>
                            </div>

                            {/* Request Part CTA */}
                            <div className="rounded-xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
                                <h3 className="mb-2 text-lg font-semibold text-slate-900">Can't Find Your Part?</h3>
                                <p className="mb-4 text-sm text-slate-600">
                                    Submit a request and let our suppliers find the exact parts you need. Get quotes from multiple verified sellers.
                                </p>
                                <Link
                                    href="#request-item-form"
                                    className="block w-full rounded-lg bg-blue-600 px-4 py-2 text-center font-medium text-white transition-colors hover:bg-blue-700"
                                >
                                    Request A Car Part
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="bg-slate-100 py-12">
                    <div className="container">
                        <div className="mx-auto max-w-4xl">
                            <h2 className="mb-8 text-center text-3xl font-bold text-slate-900">
                                Frequently Asked Questions
                            </h2>
                            <div className="space-y-4">
                                {faqStructuredData.mainEntity.map((faq, index) => (
                                    <details
                                        key={index}
                                        className="group rounded-xl border border-slate-200 bg-white shadow-sm"
                                    >
                                        <summary className="flex cursor-pointer items-start justify-between p-6 font-semibold text-slate-900">
                                            <span className="pr-4">{faq.name}</span>
                                            <svg
                                                className="h-5 w-5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-45"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M12 4v16m8-8H4"
                                                />
                                            </svg>
                                        </summary>
                                        <div className="border-t border-slate-200 p-6 pt-4 text-slate-600">
                                            {faq.acceptedAnswer.text}
                                        </div>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Related Links for Internal SEO */}
                <section className="container py-12">
                    <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
                        <h2 className="mb-6 text-center text-2xl font-bold text-slate-900">
                            Explore More Car Parts & Services
                        </h2>
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                            <Link
                                href="/car-parts-spares-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                Car Spare Parts Stores
                            </Link>
                            <Link
                                href="/scrap-yards-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                Scrap Yards
                            </Link>
                            <Link
                                href="/spare-shops-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                Spare Shops
                            </Link>
                            <Link
                                href="/midas-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                Midas Stores
                            </Link>
                            <Link
                                href="/autozone-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                AutoZone Stores
                            </Link>
                            <Link
                                href="/goldwagen-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                Goldwagen Stores
                            </Link>
                            <Link
                                href="/autostyle-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                AutoStyle Stores
                            </Link>
                            <Link
                                href="/spare-shops-near-me"
                                className="rounded-lg border border-slate-200 p-4 text-center text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-600"
                            >
                                All Businesses
                            </Link>
                        </div>
                    </div>
                </section>
            </div>

            <DownloadApp />

        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const searchProductRequest: SearchProductRequest = {
        domainCategoryIds: String(ENV.CATEGORY_ID),
        page: 1,
        size: 12,
        salesTypeId: null,
        scopeId: null,
    };

    const { data: productData } = await apolloClient.query({
        query: GET_PRODUCTS,
        variables: searchProductRequest,
    });

    // Process products to ensure all have a ratingScore
    const processedProducts = (productData?.getPrdProductList?.result ?? []).map((product: ProductItem) => {
        // Generate random rating if not provided (between 3.5 and 5.0 for realistic ratings)
        const rating = product.ratingScore && product.ratingScore > 0 
            ? product.ratingScore 
            : Math.round((Math.random() * 1.5 + 3.5) * 10) / 10;

        return {
            ...product,
            ratingScore: rating,
        };
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            products: processedProducts,
            pagination: {
                count: productData?.getPrdProductList?.count ?? 0,
                currentPage: productData?.getPrdProductList?.currentPage ?? 1,
                totalPages: productData?.getPrdProductList?.totalPages ?? 0,
            },
        },
        // Revalidate only in non-export mode (ISR not supported in export)
        ...(!isStaticExport && { revalidate: 1800 }),
    };
};
